import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BillingHistory = ({ billingHistory, onDownloadInvoice }) => {
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid': return 'text-success bg-success/10';
      case 'pending': return 'text-warning bg-warning/10';
      case 'failed': return 'text-error bg-error/10';
      case 'refunded': return 'text-muted-foreground bg-muted';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid': return 'CheckCircle';
      case 'pending': return 'Clock';
      case 'failed': return 'XCircle';
      case 'refunded': return 'RotateCcw';
      default: return 'Circle';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(amount);
  };

  const sortedHistory = [...billingHistory]?.sort((a, b) => {
    let aValue, bValue;
    
    switch (sortBy) {
      case 'date':
        aValue = new Date(a.date);
        bValue = new Date(b.date);
        break;
      case 'amount':
        aValue = a?.amount;
        bValue = b?.amount;
        break;
      case 'status':
        aValue = a?.status;
        bValue = b?.status;
        break;
      default:
        aValue = a?.date;
        bValue = b?.date;
    }

    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const SortButton = ({ field, children }) => (
    <button
      onClick={() => handleSort(field)}
      className="flex items-center space-x-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-standard"
    >
      <span>{children}</span>
      <Icon 
        name={sortBy === field ? (sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} 
        size={14} 
      />
    </button>
  );

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-card-foreground">Billing History</h2>
          <p className="text-sm text-muted-foreground">View and download your payment history</p>
        </div>
        <Button
          variant="outline"
          iconName="Download"
          iconPosition="left"
        >
          Export All
        </Button>
      </div>
      {billingHistory?.length === 0 ? (
        <div className="text-center py-8">
          <Icon name="Receipt" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground mb-2">No billing history yet</p>
          <p className="text-sm text-muted-foreground">Your payment history will appear here</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4">
                  <SortButton field="date">Date</SortButton>
                </th>
                <th className="text-left py-3 px-4">Description</th>
                <th className="text-left py-3 px-4">
                  <SortButton field="amount">Amount</SortButton>
                </th>
                <th className="text-left py-3 px-4">
                  <SortButton field="status">Status</SortButton>
                </th>
                <th className="text-left py-3 px-4">Payment Method</th>
                <th className="text-right py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedHistory?.map((transaction) => (
                <tr key={transaction?.id} className="border-b border-border hover:bg-muted/50 transition-standard">
                  <td className="py-4 px-4">
                    <div>
                      <p className="font-medium text-foreground">{formatDate(transaction?.date)}</p>
                      <p className="text-xs text-muted-foreground">Invoice #{transaction?.invoiceNumber}</p>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div>
                      <p className="font-medium text-foreground">{transaction?.description}</p>
                      <p className="text-xs text-muted-foreground">{transaction?.plan} Plan</p>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <p className="font-medium text-foreground">{formatAmount(transaction?.amount)}</p>
                  </td>
                  <td className="py-4 px-4">
                    <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(transaction?.status)}`}>
                      <Icon name={getStatusIcon(transaction?.status)} size={12} />
                      <span className="capitalize">{transaction?.status}</span>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center space-x-2">
                      <Icon name="CreditCard" size={16} className="text-muted-foreground" />
                      <span className="text-sm text-foreground">
                        {transaction?.paymentMethod?.brand} •••• {transaction?.paymentMethod?.last4}
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDownloadInvoice(transaction?.id)}
                        iconName="Download"
                        className="text-muted-foreground hover:text-foreground"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Eye"
                        className="text-muted-foreground hover:text-foreground"
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {billingHistory?.length > 0 && (
        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {billingHistory?.length} transactions
          </p>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" disabled>
              <Icon name="ChevronLeft" size={16} />
            </Button>
            <span className="text-sm text-muted-foreground">Page 1 of 1</span>
            <Button variant="outline" size="sm" disabled>
              <Icon name="ChevronRight" size={16} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingHistory;